import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useGql, Skeleton } from '@ah/design';
import { Header, PillTabs, PullToRefresh, type PillTab } from '@ah/design/mobile';
import {
  Briefcase,
  Compass,
  Newspaper,
  TrendingUp,
  Sparkles,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { INITIATIVES_Q, type Initiative } from '../shared/index.ts';

const ICONS: Record<string, LucideIcon> = {
  Briefcase,
  Compass,
  Newspaper,
  TrendingUp,
};

const TABS: PillTab[] = [
  { to: '/initiatives', label: 'Recommended for you', end: true },
  { to: '/initiatives/enrolled', label: 'Enrolled' },
];

function useInitiatives() {
  const gql = useGql();
  return useQuery({
    queryKey: ['initiatives', 'list'],
    queryFn: async () => {
      const res = await gql.request<{ initiatives: Initiative[] }>(INITIATIVES_Q);
      return res.initiatives;
    },
  });
}

function InitiativeRow({ initiative }: { initiative: Initiative }) {
  const Icon = ICONS[initiative.iconName] ?? Sparkles;
  const enrolled = initiative.viewerStatus === 'interested';
  return (
    <Link
      to={`/initiatives/${initiative.slug}`}
      className="flex items-stretch bg-card rounded-xl border border-border overflow-hidden active:bg-accent"
    >
      <div className="w-[84px] shrink-0 bg-muted/60 border-r border-border flex items-center justify-center overflow-hidden">
        {initiative.imageUrl ? (
          <img
            src={initiative.imageUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center">
            <Icon size={28} className="text-amber-700/60" strokeWidth={1.25} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1 p-4">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">
            {initiative.category}
          </span>
          {enrolled && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700 bg-emerald-100/80 rounded-full px-1.5 py-0.5">
              <Check size={10} strokeWidth={3} /> Enrolled
            </span>
          )}
        </div>
        <h2 className="text-sm font-semibold leading-snug line-clamp-2">{initiative.title}</h2>
        <p className="text-xs text-muted-foreground line-clamp-2">{initiative.tagline}</p>
      </div>
    </Link>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-stretch bg-card rounded-xl border border-border overflow-hidden">
          <Skeleton className="w-[84px] shrink-0 rounded-none" />
          <div className="flex-1 space-y-2 p-4">
            <Skeleton className="h-3 w-1/4" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function InitiativesShell({
  children,
  onRefresh,
}: {
  children: React.ReactNode;
  onRefresh: () => Promise<unknown>;
}) {
  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Initiatives" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={onRefresh}>
        {children}
      </PullToRefresh>
    </div>
  );
}

function ErrorRetry({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="text-center py-16">
      <p className="text-sm text-destructive mb-3">Could not load initiatives.</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm px-3 py-1.5 rounded-full border border-border bg-card hover:bg-accent"
      >
        Retry
      </button>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="text-center py-16 text-muted-foreground">
      <Sparkles size={32} className="mx-auto mb-2 opacity-50" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

function List({ items }: { items: Initiative[] }) {
  return (
    <div className="space-y-3">
      {items.map((i) => (
        <InitiativeRow key={i.slug} initiative={i} />
      ))}
    </div>
  );
}

export function InitiativesList() {
  const { data, isLoading, error, refetch } = useInitiatives();
  const available = data?.filter((i) => i.viewerStatus !== 'interested') ?? [];
  return (
    <InitiativesShell onRefresh={() => refetch()}>
      {isLoading ? (
        <ListSkeleton />
      ) : error ? (
        <ErrorRetry onRetry={() => refetch()} />
      ) : available.length === 0 ? (
        <EmptyState label="No initiatives available right now." />
      ) : (
        <List items={available} />
      )}
    </InitiativesShell>
  );
}

export function EnrolledInitiatives() {
  const { data, isLoading, error, refetch } = useInitiatives();
  const enrolled = data?.filter((i) => i.viewerStatus === 'interested') ?? [];
  return (
    <InitiativesShell onRefresh={() => refetch()}>
      {isLoading ? (
        <ListSkeleton />
      ) : error ? (
        <ErrorRetry onRetry={() => refetch()} />
      ) : enrolled.length === 0 ? (
        <EmptyState label="You haven't enrolled in any initiatives yet." />
      ) : (
        <List items={enrolled} />
      )}
    </InitiativesShell>
  );
}
